<template>
  <!-- The Hazards switch, first in the Maps sheet's More view: on/off for the hazard
       markers on every map on the page, with "Reported by hikers" under its title. A <label> round a native switch, so a tap anywhere
       on the row flips it and Space flips it from the keyboard. -->
  <label
    v-if="visible"
    class="hazards-switch-row"
    data-test="maps-more-hazards"
  >
    <span class="hazards-switch-row__icon" aria-hidden="true">
      <span class="material-icons">warning</span>
    </span>
    <!-- The title and a one-line description under it, as the More view's other rows. -->
    <span class="hazards-switch-row__label">
      <span class="d-block"><strong>{{ $t('trail_hazards.toggle.title') }}</strong></span>
      <small class="d-block text-muted" data-test="maps-more-hazards-subtitle">{{ $t('trail_hazards.toggle.subtitle') }}</small>
    </span>
    <span class="form-check form-switch m-0">
      <input
        class="form-check-input hazards-switch-row__toggle"
        type="checkbox"
        role="switch"
        :checked="enabled"
        :aria-label="$t('trail_hazards.toggle.a11y')"
        @change="onChange"
      />
    </span>
  </label>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import emitter from '../../../utils/bus';
import { HAZARDS_VISIBILITY_EVENT, sharedHazardLayer } from '../../../utils/trailHazards/hazardLayerController';
import { trackHazardEvent } from '../../../utils/trailHazards/hazardAnalytics';
import { hazardsSwitchState, setHazardsSwitch, showsHazardsSwitch } from '../../../utils/trailHazards/hazardsSwitch';

export default defineComponent({
  name: 'HazardsSwitchRow',

  props: {
    // The dialog's showHazards: off for maps that carry no hazard markers (the 3D peak preview).
    showHazards: { type: Boolean, default: true },
  },

  data() {
    // The More view mounts this row each time it opens, so both are read fresh then.
    return {
      available: sharedHazardLayer().isAvailable(),
      enabled: hazardsSwitchState(sharedHazardLayer()),
    };
  },

  computed: {
    visible(): boolean {
      return showsHazardsSwitch(this.showHazards, { isAvailable: () => this.available });
    },
  },

  created() {
    emitter.on(HAZARDS_VISIBILITY_EVENT, this.onVisibilityChanged);
  },

  beforeUnmount() {
    emitter.off(HAZARDS_VISIBILITY_EVENT, this.onVisibilityChanged);
  },

  methods: {
    onChange(event: Event) {
      this.enabled = setHazardsSwitch((event.target as HTMLInputElement).checked, {
        setEnabled: (next) => this.switchMarkers(next),
        track: trackHazardEvent,
      });
    },
    // Through the store when the page has the overlays module, so its mirror (what "On this
    // map" lists) moves at once; straight to the layer otherwise. Both persist the switch.
    switchMarkers(next: boolean) {
      const store = (this as any).$store;
      if (store?.state?.mapDataOverlays) return store.dispatch('mapDataOverlays/toggleHazardsOverlay', next);
      sharedHazardLayer().setEnabled(next);
      return undefined;
    },
    // The switch moved somewhere else: another map dialog on the page, or "On this map".
    onVisibilityChanged(enabled: unknown) {
      this.enabled = !!enabled;
    },
  },
});
</script>

<style scoped>
/* The More view's row and icon tile (BaseMapStylesDialog's .more-option) and Map settings'
   switch (MapSettingsView's .setting-toggle). Both are scoped to their own components, so
   the few rules this row needs are copied here. */
.hazards-switch-row {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin: 0;
  padding: 12px 0;
  border-bottom: 1px solid #2e2e2e;
  cursor: pointer;
}
.hazards-switch-row:hover { opacity: 0.82; }

.hazards-switch-row__icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #2d2d2d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ccc;
}
.hazards-switch-row__label { flex: 1; line-height: 1.4; }

.hazards-switch-row__toggle {
  width: 3em !important;
  height: 1.5em !important;
  background-color: #444 !important;
  border-color: #555 !important;
  cursor: pointer;
}
.hazards-switch-row__toggle:checked {
  background-color: #fddf00 !important;
  border-color: #fddf00 !important;
}
</style>
